import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Target, Loader2, Wallet, Mail, CheckCircle2, MessageCircle, Trophy, Info, ArrowRight, ArrowLeft, DollarSign, Shield } from "lucide-react";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function JoinHuntModal({ open, onClose, existingHunter, onRegistered }) {
  const [step, setStep] = useState(existingHunter ? 2 : 1);
  const [email, setEmail] = useState("");
  const [wallet, setWallet] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(existingHunter ? 2 : 1);
      setEmail(existingHunter?.email || "");
      setWallet("");
    }
  }, [open, existingHunter]);

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) return toast.error("Email inválido");
    if (!wallet.trim() || wallet.trim().length < 3) return toast.error("Payout wallet requerido");
    setSubmitting(true);
    try {
      const { data } = await api.post("/hunters/register", {
        email: email.trim(), payout_wallet: wallet.trim(),
      });
      toast.success(existingHunter ? "Datos actualizados" : "Ya eres Hunter, ¡a cazar!");
      onRegistered?.(data.hunter);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="join-hunt-modal" className="bg-void-modal border-neon-red/30 max-w-xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-neon-red mb-2">
            <Target className="w-3.5 h-3.5" />
            HUNTER · 10% DEL POZO · PASO {step}/2
          </div>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            {step === 1 ? "¿Cómo se gana el Hunter Jackpot?" : (existingHunter ? "Actualiza tu Hunter" : "Regístrate como Hunter")}
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Lee la mecánica del viralizador antes de registrarte. Es gratis y solo toma 30 segundos."
              : "Solo necesitamos tu correo y wallet de cobro."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-1 mt-2 mb-4">
          {[1, 2].map((n) => (
            <div key={n} className="flex-1 h-1 rounded-full overflow-hidden bg-white/10">
              <div className="h-full bg-neon-red transition-all" style={{ width: step >= n ? "100%" : "0%" }} />
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-3">
            <RuleBlock icon={Info} color="#FF003C" title="1. Regístrate gratis">
              Solo email + wallet de cobro (PayPal o CLABE). No pagas nada para participar.
            </RuleBlock>
            <RuleBlock icon={MessageCircle} color="#25D366" title="2. Comparte perfiles por WhatsApp">
              En cada tarjeta del ranking hay un botón verde "Share". Al tocarlo, WhatsApp se abre con un link único de rastreo y un mensaje pre-armado.
            </RuleBlock>
            <RuleBlock icon={Trophy} color="#FFB800" title="3. Cada click único = 1 viral point">
              Cuando alguien entra a tu link (IP distinta, 1 vez por día por perfil) tu contador sube +1. Los bots y clicks duplicados se anulan al instante.
            </RuleBlock>
            <RuleBlock icon={DollarSign} color="#00F0FF" title="4. Al cierre de 72h ganas el 10%">
              El Hunter con más viral_points se lleva el 10% del pozo total (empates → primero en llegar). Pago a tu wallet en 48h hábiles.
            </RuleBlock>
            <RuleBlock icon={Shield} color="#888" title="5. Reglas anti-fraude">
              Un clic por IP por día. Múltiples registros con el mismo teléfono son baneados sin devolución.
            </RuleBlock>
            <Button data-testid="hunt-step1-next" onClick={() => setStep(2)}
              className="w-full bg-neon-red hover:bg-neon-red/90 text-white font-bold rounded-full py-6 neon-red-glow">
              Entendido, registrarme <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}

        {step === 2 && (
          <>
            {existingHunter && (
              <div className="rounded-xl border border-neon-red/30 bg-neon-red/5 p-3 flex items-start gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-neon-red mt-0.5 flex-shrink-0" />
                <div className="text-xs">
                  <div className="font-mono uppercase tracking-widest text-neon-red mb-0.5">Activo</div>
                  <div className="text-white/90">
                    {existingHunter.viral_points_today ?? 0} viral · {existingHunter.unique_clicks_today ?? 0} unique clicks hoy
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Email
                </Label>
                <Input data-testid="hunter-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com"
                  className="mt-1.5 bg-black border-white/10" />
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-widest text-neon-red flex items-center gap-1.5">
                  <Wallet className="w-3 h-3" /> Payout Wallet (PayPal / CLABE)
                </Label>
                <Input data-testid="hunter-wallet-input" value={wallet}
                  onChange={(e) => setWallet(e.target.value)} placeholder="PayPal email o cuenta bancaria"
                  className="mt-1.5 bg-black border-white/10" />
                <p className="text-[11px] text-muted-foreground mt-1.5">Se guarda cifrado.</p>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {!existingHunter && (
                <Button variant="outline" onClick={() => setStep(1)} className="border-white/15 rounded-full">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              )}
              <Button data-testid="hunter-submit-button" disabled={submitting} onClick={submit}
                className="flex-1 bg-neon-red hover:bg-neon-red/90 text-white font-bold rounded-full py-6 neon-red-glow">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                  (existingHunter ? "Actualizar Hunter" : (<><Target className="w-4 h-4 mr-2" /> Ser Hunter y cazar</>))}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RuleBlock({ icon: Icon, color, title, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl border border-white/5 bg-black/50">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}15`, border: `1px solid ${color}40`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1">
        <div className="font-heading font-bold text-sm mb-0.5" style={{ color }}>{title}</div>
        <p className="text-xs text-white/80 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}
